import React, { useCallback, useEffect, useState } from 'react';
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button } from '@mui/material';
import { useAppSettings } from '../../context/SettingsContext';
import { useBackendAuth } from '../../services/apiClient';
import xeroAuth from '../../services/xeroAuth';
import { fetchXeroStatus, xeroAuthorizeUrl } from '../../services/xeroApi';
import { XeroHero } from '../../components/xero/xeroShared';

const XeroLayout: React.FC = () => {
  const { settings } = useAppSettings();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const backend = useBackendAuth;
  const [connected, setConnected] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(true);
  const justConnected = searchParams.get('xero') === 'connected';
  const xeroError = searchParams.get('xero_error');
  const hasCreds = Boolean(settings.integrations.xero.clientId.trim());

  const check = useCallback(async () => {
    setChecking(true);
    if (backend) {
      try {
        const s = await fetchXeroStatus();
        setConnected(s.connected);
      } catch {
        setConnected(false);
      }
    } else {
      if (!hasCreds) {
        setConnected(false);
      } else {
        try {
          await xeroAuth.ensureValidAccessToken();
          setConnected(xeroAuth.isAuthenticated());
        } catch {
          setConnected(false);
        }
      }
    }
    setChecking(false);
  }, [backend, hasCreds]);

  useEffect(() => {
    void check();
  }, [check]);

  useEffect(() => {
    if (searchParams.get('xero') !== 'connected') {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.delete('xero');
    setSearchParams(next, { replace: true });
    void check();
  }, [searchParams, setSearchParams, check]);

  const connect = () => {
    if (backend) {
      window.location.assign(xeroAuthorizeUrl());
    } else {
      navigate('/settings#xero');
    }
  };

  if (!checking && connected === false) {
    return (
      <Box>
        <XeroHero subtitle="Connect once to view reports, invoices, and follow up on overdue accounts receivable." />
        {xeroError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Connection failed:</strong> {decodeURIComponent(xeroError)}
            {xeroError.includes('redirect_uri') && (
              <Box sx={{ mt: 1, fontSize: '0.9em' }}>
                Make sure the redirect URI in your{' '}
                <a
                  href="https://developer.xero.com/app/manage"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', fontWeight: 700 }}
                >
                  Xero Developer Portal
                </a>{' '}
                exactly matches: <code>https://growth.iwizz.com.au/api/xero/callback</code>
              </Box>
            )}
            {xeroError.includes('invalid_scope') && (
              <Box sx={{ mt: 1, fontSize: '0.9em' }}>
                The requested scopes may not be enabled for your Xero app. Check the app permissions in the{' '}
                <a
                  href="https://developer.xero.com/app/manage"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', fontWeight: 700 }}
                >
                  Xero Developer Portal
                </a>
                .
              </Box>
            )}
          </Alert>
        )}
        {justConnected && (
          <Alert severity="info" sx={{ mb: 2 }}>
            OAuth completed — finishing setup…
          </Alert>
        )}
        <Alert
          severity="warning"
          action={
            <Button variant="contained" size="small" onClick={connect}>
              {hasCreds ? 'Connect Xero' : 'API settings'}
            </Button>
          }
          sx={{ mb: 2 }}
        >
          {hasCreds
            ? xeroAuth.hasStoredSession()
              ? 'Your Xero session expired — click Connect Xero to re-authorise (your Client ID and Secret are still saved).'
              : 'Authorise your Xero organisation to unlock reports, invoices, and payment reminders.'
            : 'Add Xero credentials in API settings, then connect your organisation.'}
        </Alert>
        {hasCreds && (
          <Button sx={{ mt: 1 }} variant="outlined" onClick={() => navigate('/settings#xero')}>
            API settings
          </Button>
        )}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>First time setup:</strong>
          </Alert>
          <ol style={{ margin: 0, paddingLeft: '1.5em' }}>
            <li>
              Go to{' '}
              <Button size="small" variant="text" onClick={() => navigate('/settings#xero')}>
                API Settings → Xero
              </Button>
            </li>
            <li>Add your Xero Client ID and Client Secret</li>
            <li>
              Verify the redirect URI in{' '}
              <a href="https://developer.xero.com/app/manage" target="_blank" rel="noopener noreferrer">
                Xero Developer Portal
              </a>{' '}
              matches: <code style={{ fontSize: '0.9em' }}>https://growth.iwizz.com.au/api/xero/callback</code>
            </li>
            <li>Click "Connect Xero" to authorize</li>
          </ol>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1280 }}>
      {justConnected && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Xero connected — your session is saved.
        </Alert>
      )}
      <Outlet context={{ connected, refreshConnection: check }} />
    </Box>
  );
};

export default XeroLayout;

export type XeroOutletContext = {
  connected: boolean | null;
  refreshConnection: () => Promise<void>;
};
